import {useState, useEffect, useMemo} from 'react';
import { useNavigate } from 'react-router-dom';
import getAdminTaskInstances from '../functions/API/getAdminTaskInstances.js'
import postAssignTaskInstance from '../functions/API/postAssignTaskInstance.js'
import postUpdateTaskInstanceStatus from '../functions/API/postUpdateTaskInstanceStatus.js'
import postDeleteTaskInstance from '../functions/API/postDeleteTaskInstance.js'
import getAPI from '../functions/API/getAPI.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import NewTaskInstanceForm from '../forms/newTaskInstance.js';
import SlideOverRight from '../components/slideOverRight.js';
import Loader from '../components/loader.js'
import ConfirmationModal from '../modals/confirmation.js'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { ChevronDown, MoreVertical, Eye, UserPlus, Play, CheckCircle2, XCircle, Clock, MessageSquare, Search, Star, Trash2 } from 'lucide-react'
import { INSTANCE_STATUSES, SOURCE_TYPES, ACTIVE_STATUSES } from '../constants/taskInstanceStatuses.js'

export default function TaskInstances() {
    const navigate = useNavigate();
    const [newInstanceForm, setNewInstanceForm] = useState(false);
    const [instances, setInstances] = useState([]);
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [agentFilter, setAgentFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [actionLoading, setActionLoading] = useState({});
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [instanceToDelete, setInstanceToDelete] = useState(null);

    const agents = useMemo(() => {
        const map = new Map();
        instances.forEach(i => {
            if (i.owner_admin_user_id && i.owner_admin_name) {
                map.set(i.owner_admin_user_id, i.owner_admin_name);
            }
        });
        return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [instances]);

    async function GetAPIData() {
        const [instancesRes, userRes] = await Promise.all([
            getAdminTaskInstances(),
            getAPI('userdetails'),
        ]);

        if (instancesRes.response.ok && instancesRes.data?.task_instances) {
            setInstances(instancesRes.data.task_instances);
        } else {
            if (!instancesRes.response.ok) {
                toast.error('Failed to load task instances.');
            }
            setInstances([]);
        }

        if (userRes.response.ok && userRes.data?.id) {
            setCurrentUserId(userRes.data.id);
        }

        setIsLoaded(true);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh]);

    function getFilteredInstances() {
        let filtered = instances;

        // Agent filter
        if (agentFilter === 'mine') {
            filtered = filtered.filter(i => i.owner_admin_user_id === currentUserId);
        } else if (agentFilter === 'unassigned') {
            filtered = filtered.filter(i => !i.owner_admin_user_id);
        } else if (agentFilter !== 'all') {
            filtered = filtered.filter(i => i.owner_admin_user_id === agentFilter);
        }

        // Status filter
        if (statusFilter === 'active') {
            filtered = filtered.filter(i => ACTIVE_STATUSES.includes(i.status));
        } else if (statusFilter !== 'all') {
            filtered = filtered.filter(i => i.status === statusFilter);
        }

        // Search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(i =>
                (i.tenant_user_name && i.tenant_user_name.toLowerCase().includes(query)) ||
                (i.tenant_address && i.tenant_address.toLowerCase().includes(query)) ||
                (i.tenant_user_email && i.tenant_user_email.toLowerCase().includes(query)) ||
                (i.task_flow_name && i.task_flow_name.toLowerCase().includes(query))
            );
        }

        return filtered;
    }

    function getAgentLabel() {
        if (agentFilter === 'all') return 'All Agents';
        if (agentFilter === 'mine') return 'My Tasks';
        if (agentFilter === 'unassigned') return 'Unassigned';
        const agent = agents.find(a => a.id === agentFilter);
        return agent ? agent.name : 'All Agents';
    }

    function getStatusLabel() {
        if (statusFilter === 'all') return 'All Statuses';
        if (statusFilter === 'active') return 'Active';
        const status = INSTANCE_STATUSES[statusFilter];
        return status ? status.label : statusFilter;
    }

    async function handleTakeOwnership(instance) {
        if (actionLoading[instance.id]) return;
        setActionLoading({...actionLoading, [instance.id]: true});

        const response = await postAssignTaskInstance({
            id: instance.id,
            owner_admin_user_id: currentUserId,
        });

        if (response.response.ok) {
            toast.success('Task assigned to you.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to assign task.');
        }
        setActionLoading({...actionLoading, [instance.id]: false});
    }

    async function handleStatusChange(instance, newStatus) {
        if (actionLoading[instance.id]) return;
        setActionLoading({...actionLoading, [instance.id]: true});

        const response = await postUpdateTaskInstanceStatus({
            id: instance.id,
            status: newStatus,
        });

        if (response.response.ok) {
            toast.success('Status updated.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to update status.');
        }
        setActionLoading({...actionLoading, [instance.id]: false});
    }

    async function handleDeleteInstance() {
        if (!instanceToDelete) return;

        const response = await postDeleteTaskInstance({ id: instanceToDelete.id });

        if (response.response.ok) {
            toast.success('Task instance deleted.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to delete task instance.');
        }
        setInstanceToDelete(null);
    }

    function formatDate(dateStr) {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString();
    }

    const filteredInstances = getFilteredInstances();

    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div className="">
                        <div className="sm:flex sm:items-center">
                            <div className="sm:flex-auto">
                                <h1 className="text-2xl text-left leading-6 text-gray-900 dark:text-white col-span-3">Task Instances</h1>
                                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                    Manage and track all task instances across tenants
                                </p>
                            </div>
                            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                                <Button onClick={() => setNewInstanceForm(true)}>
                                    Create task instance
                                </Button>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, email or address..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-9 w-full rounded-md border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 dark:text-white pl-9 pr-3 text-sm shadow-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-700"
                                />
                            </div>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline">
                                        {getAgentLabel()}
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem onSelect={() => setAgentFilter('all')}>
                                        All Agents
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setAgentFilter('mine')}>
                                        My Tasks
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setAgentFilter('unassigned')}>
                                        Unassigned
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {agents.map((agent) => (
                                        <DropdownMenuItem key={agent.id} onSelect={() => setAgentFilter(agent.id)}>
                                            {agent.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline">
                                        Status: {getStatusLabel()}
                                        <ChevronDown className="ml-2 h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem onSelect={() => setStatusFilter('active')}>
                                        Active
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setStatusFilter('all')}>
                                        All Statuses
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {Object.entries(INSTANCE_STATUSES).map(([key, val]) => (
                                        <DropdownMenuItem key={key} onSelect={() => setStatusFilter(key)}>
                                            {val.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        <div className="mt-8 flow-root">
                            <div className="overflow-x-auto">
                                <div className="inline-block min-w-full py-2 align-middle lg:pr-2">
                                    <table className="min-w-full divide-y divide-gray-300 dark:divide-zinc-700 border border-gray-200 dark:border-zinc-700 rounded-lg">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Request
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Assigned To
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Status
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Progress
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Due Date
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Source
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Created
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Rating
                                                </th>
                                                <th scope="col" className="py-2.5 px-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    <MessageSquare className="h-4 w-4 mx-auto text-gray-500" />
                                                </th>
                                                <th scope="col" className="relative py-2.5 pl-3 pr-4 sm:pr-0">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-zinc-700 bg-white dark:bg-transparent">
                                            {filteredInstances.map((instance) => {
                                                const statusInfo = INSTANCE_STATUSES[instance.status] || { label: instance.status, color: 'secondary' };
                                                const sourceInfo = SOURCE_TYPES[instance.source] || { label: instance.source };

                                                return (
                                                    <tr key={instance.id} onClick={() => navigate(`/taskinstances/${instance.id}`)} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 ${instance.status === 'AWAITING_ADMIN' ? 'bg-amber-50/50 dark:bg-amber-900/20' : ''}`}>
                                                        <td className={`px-3 py-2.5 text-xs ${instance.status === 'AWAITING_ADMIN' ? 'border-l-2 border-l-amber-400' : ''}`}>
                                                            <div className="font-medium text-gray-900 dark:text-white">
                                                                {instance.task_flow_name}
                                                            </div>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                {instance.tenant_user_name}{instance.tenant_address ? ` — ${instance.tenant_address}` : ''}
                                                            </div>
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {instance.owner_admin_user_id ? instance.owner_admin_name : (
                                                                <span className="text-amber-600 dark:text-amber-400">Unassigned</span>
                                                            )}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs">
                                                            <Badge variant={statusInfo.color}>{statusInfo.label}</Badge>
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {instance.progress_percentage}%
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {formatDate(instance.due_date)}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {sourceInfo.label}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400">
                                                            {formatDate(instance.created_at)}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-center">
                                                            {instance.feedback_rating ? (
                                                                <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-300">
                                                                    <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                                                                    {instance.feedback_rating}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-300 dark:text-gray-600">-</span>
                                                            )}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs text-center">
                                                            {instance.unread_message_count > 0 ? (
                                                                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-400 ring-1 ring-inset ring-red-600/20">
                                                                    <MessageSquare className="h-3 w-3" />
                                                                    {instance.unread_message_count}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-300 dark:text-gray-600">-</span>
                                                            )}
                                                        </td>
                                                        <td className="relative whitespace-nowrap py-2.5 pl-3 pr-4 text-right text-xs font-medium sm:pr-0" onClick={(e) => e.stopPropagation()}>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="ghost" size="icon">
                                                                        <MoreVertical className="h-4 w-4" />
                                                                        <span className="sr-only">Open menu</span>
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onSelect={() => navigate(`/taskinstances/${instance.id}`)}>
                                                                        <Eye className="mr-2 h-4 w-4" />
                                                                        View Details
                                                                    </DropdownMenuItem>
                                                                    {!instance.owner_admin_user_id && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onSelect={() => handleTakeOwnership(instance)}
                                                                                disabled={actionLoading[instance.id]}
                                                                            >
                                                                                <UserPlus className="mr-2 h-4 w-4" />
                                                                                Take Ownership
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                    <DropdownMenuSeparator />
                                                                    {instance.status === 'NOT_STARTED' && (
                                                                        <DropdownMenuItem
                                                                            onSelect={() => handleStatusChange(instance, 'IN_PROGRESS')}
                                                                            disabled={actionLoading[instance.id]}
                                                                        >
                                                                            <Play className="mr-2 h-4 w-4" />
                                                                            Start
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    {(instance.status === 'IN_PROGRESS' || instance.status === 'AWAITING_ADMIN') && (
                                                                        <DropdownMenuItem
                                                                            onSelect={() => handleStatusChange(instance, 'COMPLETED')}
                                                                            disabled={actionLoading[instance.id]}
                                                                        >
                                                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                                                            Complete
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    {instance.status !== 'COMPLETED' && instance.status !== 'CANCELLED' && (
                                                                        <DropdownMenuItem
                                                                            onSelect={() => handleStatusChange(instance, 'CANCELLED')}
                                                                            disabled={actionLoading[instance.id]}
                                                                            className="text-red-600 focus:text-red-600"
                                                                        >
                                                                            <XCircle className="mr-2 h-4 w-4" />
                                                                            Cancel
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem
                                                                        onSelect={() => { setInstanceToDelete(instance); setDeleteModalOpen(true); }}
                                                                        className="text-red-600 focus:text-red-600"
                                                                    >
                                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                                        Delete
                                                                    </DropdownMenuItem>
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {filteredInstances.length === 0 && (
                                        <div className="text-center py-12">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">No task instances found.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <ConfirmationModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    title="Delete Task Instance"
                    message={`Are you sure you want to permanently delete "${instanceToDelete?.task_flow_name}" for ${instanceToDelete?.tenant_user_name}? All steps, fields, messages, and history will be removed. This action cannot be undone.`}
                    note="Any files uploaded to SharePoint will not be deleted and must be removed manually."
                    confirmText="Delete"
                    cancelText="Go Back"
                    onConfirm={handleDeleteInstance}
                    variant="danger"
                />
                <SlideOverRight visible={newInstanceForm} setVisible={setNewInstanceForm}>
                    <NewTaskInstanceForm
                        setVisible={setNewInstanceForm}
                        refresh={setShouldRefresh}
                        refreshValue={shouldRefresh}
                    />
                </SlideOverRight>
            </AdminSidebar>
        </>
    );
}
